import { ApolloError } from '@apollo/client';
import useApolloError, { ApolloErrorKind } from '@website/hooks/useApolloError';
import { useContext } from 'react';
import { Container } from 'react-bootstrap';
import { Store } from '../../Store';
import RequiresAuth from '../auth/RequiresAuth';
import LoadingPage from './LoadingPage';
import NotFound from './NotFound';

export function getStringError(
  error: object | Error | ApolloError | string,
): string {
  try {
    if (error instanceof ApolloError) {
      return error.message;
    }

    if (error instanceof Error) {
      return error.message;
    }

    if (typeof error === 'object') {
      return JSON.stringify(error);
    }

    // string, return error as is
    return error;
  } catch (e) {
    return '';
  }
}

/**
 * Permission denied error message
 *
 * This usually happens when a user signs in with the wrong email address.
 */
export function PermissionDeniedErrorMessage() {
  const { state } = useContext(Store);

  // Handle still authenticating
  if (state.isAuthenticating) {
    return <LoadingPage />;
  }

  // Handle not signed in by requesting it
  if (!state.isAuthenticated) {
    return (
      <RequiresAuth authModalTitle="Please sign in to view this">
        <Container className="pt-3">
          <h3>You're not signed in.</h3>{' '}
        </Container>
      </RequiresAuth>
    );
  }

  return (
    <Container className="pt-3">
      <h3>Sorry - you may be signed in with the wrong account.</h3>

      <p>
        You don't have permission to view this page. This usually happens when
        you're signed in with the wrong email address or Google/Facebook
        account.
      </p>

      <p>
        You're currently signed in with the email address{' '}
        <strong>{state.user.email}</strong>. It's likely that you have an
        account registered with a different email address. You can try signing
        out, and then in again, from the menu at the top of this page.
      </p>

      <p>
        By the way, if you've followed a link from an email, you could try the
        email address that received this email.
      </p>
    </Container>
  );
}

/**
 * Error Page
 *
 * __Note:__ Where possible it is nice to add the error message to this component, so that we can debug any errors more
 * easily that occur.
 */
export default function ErrorPage({
  error,
}: {
  error?: object | ApolloError | Error | string;
}) {
  const errorMessage = getStringError(error);

  // determine the kind of error thrown by apollo
  const errorKind = useApolloError(error);

  if (errorKind === ApolloErrorKind.NotFound) {
    return <NotFound />;
  }

  if (errorKind === ApolloErrorKind.PermissionDenied) {
    return <PermissionDeniedErrorMessage />;
  }

  if (errorKind === ApolloErrorKind.NetworkError) {
    return (
      <Container className="pt-3">
        <h3>
          Sorry, internet connection lost - please retry when you are connected.
        </h3>
      </Container>
    );
  }

  return (
    <Container className="pt-3">
      <h3>Sorry, an error has occurred!</h3>
      {errorMessage && (
        <>
          <h4>Please quote the following message when contacting us:</h4>
          <blockquote> {errorMessage} </blockquote>
        </>
      )}
    </Container>
  );
}
